import { memo, useState, useRef, useEffect, useCallback } from 'react'
import { useResponsive } from 'hooks/useResponsive'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { AIVideoSocketMessageStatus } from 'types/aiVideo'
import AISkeletonItem from 'components/AISkeletonItem'
import useAutoPlayWhenVisible from './useAutoPlayWhenVisible'
import * as Styled from './styled'
import { useIsInViewport } from 'hooks/useIsInViewport'
import { useGenerationProgress } from 'hooks/useGenerationProgress'
import { isLoadingFinalMandatoryValue } from 'utils'
import { normalizeAspectRatio, SQUARE_ASPECT_RATIO } from 'utils/aspectRatio'
import VolumeIcon from 'assets/svgr-icons/Volume.svg'
import VolumeMuteIcon from 'assets/svgr-icons/VolumeMute.svg'

const VideoItem: React.FC<{
  videoUrl?: string | null
  status: AIVideoSocketMessageStatus | null
  additionalContent?: React.ReactNode
  assetId?: string | number
  aspectRatio?: string
  supportsAudio?: boolean
}> = ({
  videoUrl,
  status,
  additionalContent,
  assetId,
  aspectRatio = SQUARE_ASPECT_RATIO,
  supportsAudio = false
}) => {
  const [isVideoLoading, setIsVideoLoading] = useState(true)
  const isGenerating = status === AIVideoSocketMessageStatus.PROCESSING
  const { progress, message } = useGenerationProgress({
    assetId,
    isGenerating
  })
  const showLoading = isGenerating || isLoadingFinalMandatoryValue(progress)

  const aspectRatioValue = normalizeAspectRatio(aspectRatio)

  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isInViewPort = useIsInViewport(containerRef, {
    rootMargin: '0px 0px 400px 0px'
  })
  const [shouldRenderVideo, setShouldRenderVideo] = useState(false)
  const [isMuted, setIsMuted] = useState(true)

  const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)
  useAutoPlayWhenVisible(containerRef, videoRef, isVideoLoading)

  const handleVideoLoad = () => setIsVideoLoading(false)

  const handleMouseEnter = () => {
    if (!isMobile && videoRef.current && !isVideoLoading) {
      videoRef.current.play()
    }
  }

  const handleMouseLeave = () => {
    if (!isMobile && videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
  }

  const toggleMute = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted
      setIsMuted(videoRef.current.muted)
    }
  }, [])

  useEffect(() => {
    if (isInViewPort && !shouldRenderVideo) {
      setShouldRenderVideo(true)
    }
  }, [isInViewPort, shouldRenderVideo])

  return (
    <Styled.VideoItemContainer ref={containerRef}>
      {shouldRenderVideo ? (
        showLoading ? (
          <Styled.LoadingWrapper
            overlay
            progress={progress}
            message={message}
            aspectRatio={aspectRatioValue}
          />
        ) : (
          <Styled.VideoWrapper
            $aspectRatio={aspectRatioValue}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {isVideoLoading && <AISkeletonItem width="100%" height="100%" />}
            <Styled.VideoElement
              ref={videoRef}
              src={videoUrl ?? undefined}
              onLoadedData={handleVideoLoad}
              $isLoading={isVideoLoading}
              $aspectRatio={aspectRatioValue}
              crossOrigin="anonymous"
              muted
              loop
              playsInline
            />
            {!isVideoLoading && supportsAudio && (
              <Styled.MuteToggleButton onClick={toggleMute}>
                {isMuted ? <VolumeMuteIcon /> : <VolumeIcon />}
              </Styled.MuteToggleButton>
            )}
            {additionalContent}
          </Styled.VideoWrapper>
        )
      ) : (
        <Styled.VideoPlaceHolder>
          <AISkeletonItem width="100%" height="345px" />
        </Styled.VideoPlaceHolder>
      )}
    </Styled.VideoItemContainer>
  )
}

export default memo(VideoItem)
